import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Boxes, Globe2, HeartHandshake, IndianRupee, MapPin, Package, PackageCheck,
  Phone, Radar, Shuffle, Truck, User, Zap,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VideoHeroBg from "../components/service/VideoHeroBg";
import { usePageMeta } from "../components/service/ServiceBits";
import { PartnersMarquee } from "../components/home/ContentSections";
import { useCountUp, useInView } from "../components/home/Sections";
import { BG } from "../components/MovingBg";
import api from "../lib/api";

const ROAD_POSTER = "/videos/road_poster.jpg";
const BIKE_POSTER = "/videos/bike_poster.jpg";

const VALUES = [
  { icon: HeartHandshake, title: "Customer First", desc: "Every decision starts with what makes shipping easier for you." },
  { icon: IndianRupee, title: "Radical Transparency", desc: "Itemised GST billing, clear quotes, zero hidden surcharges." },
  { icon: Zap, title: "Speed With Care", desc: "Fastest possible routing without compromising package safety." },
  { icon: Globe2, title: "Made in India, Global by Heart", desc: "Indian roots, worldwide reach — 220+ countries served." },
];

const JOURNEY = [
  { icon: User, name: "Customer", desc: "You book online in 60 seconds" },
  { icon: Package, name: "Pickup", desc: "Doorstep collection scheduled" },
  { icon: Boxes, name: "Processing", desc: "Weighed, measured & secured" },
  { icon: Shuffle, name: "Courier Allocation", desc: "Best courier auto-selected" },
  { icon: Truck, name: "Transit", desc: "Moves by road, air or sea" },
  { icon: Radar, name: "Tracking", desc: "Scan-level live updates" },
  { icon: PackageCheck, name: "Delivery", desc: "Doorstep delivery with proof" },
];

const TRUST = [
  "Domestic & international courier under one roof",
  "Multiple courier networks — DHL, FedEx, UPS, Aramex & more",
  "Business & eCommerce shipping programs",
  "Transparent pricing with GST-compliant billing",
  "Live scan-level tracking on every shipment",
  "Real human support on call and WhatsApp",
];

function Stat({ value, label, start, testid }) {
  const v = useCountUp(value, start);
  const display = value >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v.toLocaleString("en-IN");
  return (
    <div data-testid={testid} className="border-l border-white/15 pl-5 first:border-l-0 first:pl-0 sm:pl-8 sm:first:pl-0">
      <p className="font-heading text-4xl font-light tracking-tight text-white sm:text-5xl">
        {display}<span className="text-saffron">+</span>
      </p>
      <p className="mt-2 text-xs font-semibold text-slate-400">{label}</p>
    </div>
  );
}

export default function AboutUs() {
  const [home, setHome] = useState(null);
  usePageMeta(
    "About IndiaFast Global Couriers | Moving India Closer to the World",
    "IndiaFast is a courier and logistics brand making domestic and international shipping easier, transparent and accessible — 29,000+ pincodes, 220+ countries.",
    "/p/about"
  );
  useEffect(() => {
    api.get("/public/home").then((r) => setHome(r.data)).catch(() => setHome({}));
  }, []);
  const stats = home?.stats;
  const [journeyRef, journeyInView] = useInView();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />

      {/* HERO — branded video */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden bg-navy pb-20 pt-32" data-testid="about-hero">
        <VideoHeroBg mp4="/videos/road.mp4" webm="/videos/road.webm" poster={ROAD_POSTER}
                     posterAlt="IndiaFast branded trucks on the move" badge="India → World" testid="about-hero-video" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6">
          <p className="animate-fade-up flex items-center gap-2 text-xs font-semibold text-slate-300">
            <span className="h-2 w-2 rounded-full bg-saffron" /> About IndiaFast
          </p>
          <h1 className="animate-fade-up stagger-1 mt-5 max-w-3xl font-heading text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-6xl">
            Moving India<br />closer to <span className="text-saffron">the world.</span>
          </h1>
          <p className="animate-fade-up stagger-2 mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            INDIAFAST is a courier and logistics brand providing domestic and international shipping
            solutions for individuals, families, businesses, eCommerce sellers and organizations.
          </p>
          <div className="animate-fade-up stagger-3 mt-8 flex flex-wrap gap-3">
            <Link to="/rate-calculator" data-testid="about-quote-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-saffron py-2 pl-6 pr-2 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
              Get a Quote
              <span className="grid h-8 w-8 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <a href="tel:9783807838" data-testid="about-call-btn"
               className="group inline-flex items-center gap-2 rounded-full border border-white/25 py-2 pl-6 pr-2 text-sm font-bold text-white transition-colors hover:border-saffron hover:text-saffron">
              <Phone className="h-4 w-4" /> Talk to Our Team
              <span className="grid h-8 w-8 place-items-center rounded-full bg-saffron/20 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
                <ArrowRight className="h-4 w-4" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="bg-navy-dark py-16 text-white sm:py-20" data-testid="about-stats">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <span className="h-2 w-2 rounded-full bg-saffron" /> IndiaFast in numbers
          </p>
          <StatsRow stats={stats} />
        </div>
      </section>

      {/* WHO WE ARE — statement + collage */}
      <section className="bg-white py-20 sm:py-28" data-testid="about-story">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <span className="h-2 w-2 rounded-full bg-saffron" /> Who we are
              </p>
              <h2 className="mt-5 font-heading text-3xl font-medium leading-[1.08] tracking-tight text-navy sm:text-5xl">
                One brand.<br />Every shipment.<br /><span className="text-saffron">Zero guesswork.</span>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Our objective is simple: make courier shipping easier, more transparent and more
                accessible for everyone. From a student's documents to an exporter's full container —
                IndiaFast moves it with the same care, tracking and honesty.
              </p>
              <Link to="/branches" data-testid="about-branches-btn"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white py-2 pl-6 pr-2 text-sm font-bold text-navy transition-colors hover:border-saffron hover:text-saffron">
                Find a branch near you
                <span className="grid h-8 w-8 place-items-center rounded-full bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
                  <MapPin className="h-4 w-4" />
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:col-span-6">
              <div className="overflow-hidden rounded-3xl shadow-xl shadow-stone-200">
                <img src={BG.warehouse} alt="IndiaFast fulfilment warehouse" loading="lazy"
                     className="kenburns-a h-64 w-full object-cover sm:h-80" />
              </div>
              <div className="mt-8 overflow-hidden rounded-3xl shadow-xl shadow-stone-200">
                <img src={BIKE_POSTER} alt="IndiaFast rider with branded delivery box" loading="lazy"
                     className="kenburns-b h-64 w-full object-cover sm:h-80" />
              </div>
              <div className="col-span-2 -mt-4 overflow-hidden rounded-3xl shadow-xl shadow-stone-200">
                <img src={BG.port} alt="IndiaFast global port operations" loading="lazy"
                     className="kenburns-a h-48 w-full object-cover sm:h-56" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F4F3F1] py-20 sm:py-28" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span className="h-2 w-2 rounded-full bg-saffron" /> What drives us
          </p>
          <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-navy sm:text-5xl">
            Built on four promises.
          </h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <div key={v.title} data-testid={`about-value-${i}`}
                   className="group rounded-2xl border border-stone-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl hover:shadow-stone-200">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW EVERY SHIPMENT MOVES — animated flow */}
      <section ref={journeyRef} className="overflow-hidden bg-white py-20 sm:py-28" data-testid="about-journey">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span className="h-2 w-2 rounded-full bg-saffron" /> How every shipment moves
          </p>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-medium tracking-tight text-navy sm:text-5xl">
            Customer → Pickup → Processing → Transit → <span className="text-saffron">Delivery.</span>
          </h2>

          <div className={`mt-16 transition-all duration-700 ${journeyInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="relative hidden lg:block">
              <div className="absolute left-0 right-0 top-7 h-0.5 bg-stone-200" />
              <div className="journey-line absolute left-0 top-7 h-0.5 bg-gradient-to-r from-saffron via-saffron to-emerald-500" />
              <div className="journey-dot absolute top-[1.15rem] z-10 grid h-9 w-9 place-items-center rounded-full bg-navy text-saffron shadow-lg shadow-navy/30">
                <Package className="h-4 w-4" />
              </div>
              <div className="grid grid-cols-7 gap-3">
                {JOURNEY.map((s, i) => (
                  <div key={s.name} className="relative pt-16 text-center" data-testid={`about-journey-step-${i}`}>
                    <span className={`absolute left-1/2 top-4 z-10 grid h-7 w-7 -translate-x-1/2 place-items-center rounded-full border-2 bg-white text-[10px] font-black transition-colors ${journeyInView ? "border-saffron text-saffron" : "border-stone-300 text-slate-400"}`}
                          style={{ transitionDelay: `${i * 400}ms` }}>
                      {i + 1}
                    </span>
                    <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-navy text-white transition-colors hover:bg-saffron">
                      <s.icon className="h-5 w-5" />
                    </span>
                    <p className="mt-3 font-heading text-[13px] font-bold tracking-wide text-navy">{s.name}</p>
                    <p className="mx-auto mt-1.5 max-w-[10rem] text-[11px] leading-relaxed text-slate-500">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:hidden">
              <div className="relative ml-5 border-l-2 border-dashed border-saffron/40 pl-8">
                {JOURNEY.map((s, i) => (
                  <div key={s.name} className="relative pb-7 last:pb-0" data-testid={`about-journey-step-m-${i}`}>
                    <span className="absolute -left-[2.65rem] grid h-10 w-10 place-items-center rounded-2xl bg-navy text-white shadow-lg">
                      <s.icon className="h-4 w-4" />
                    </span>
                    <p className="font-heading text-sm font-bold tracking-wide text-navy">{s.name}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CUSTOMERS TRUST */}
      <section className="bg-navy py-20 text-white sm:py-28" data-testid="about-trust">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <span className="h-2 w-2 rounded-full bg-saffron" /> Why customers trust IndiaFast
            </p>
            <h2 className="mt-4 font-heading text-3xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
              Trusted for the<br /><span className="text-saffron">long haul.</span>
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              Thousands of families, sellers and businesses ship with IndiaFast because we keep
              every promise measurable — price, time and proof of delivery.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {TRUST.map((t, i) => (
              <div key={t} data-testid={`about-trust-${i}`}
                   className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-colors hover:border-saffron/50">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-saffron/15 text-saffron">
                  <PackageCheck className="h-4 w-4" />
                </span>
                <p className="text-sm font-semibold leading-relaxed text-slate-200">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PartnersMarquee partners={home?.partners} />

      {/* CTA */}
      <section className="bg-[#F2F1EE] py-20 sm:py-24" data-testid="about-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-medium leading-[1.08] tracking-tight text-navy sm:text-5xl">
            Ready to ship with a team<br /><span className="text-saffron">that treats it like their own?</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="about-cta-start-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-saffron py-2 pl-6 pr-2 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-colors hover:bg-saffron-hover">
              Start Shipping
              <span className="grid h-8 w-8 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <Link to="/track" data-testid="about-cta-track-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:border-saffron hover:text-saffron">
              Track a Shipment
            </Link>
          </div>
        </div>
      </section>

      <Footer contact={home?.contact} />
    </div>
  );
}

function StatsRow({ stats }) {
  const [ref, inView] = useInView();
  const s = stats || {};
  return (
    <div ref={ref} className="mt-10 grid grid-cols-2 gap-8 sm:grid-cols-4" data-testid="about-stats-row">
      <Stat value={s.countries_served || 220} label="Countries served" start={inView} testid="about-stat-countries" />
      <Stat value={s.pincodes_covered || 29000} label="Pincodes covered" start={inView} testid="about-stat-pincodes" />
      <Stat value={s.shipments_delivered || 10000000} label="Shipments delivered" start={inView} testid="about-stat-shipments" />
      <Stat value={s.courier_partners || 17} label="Courier partners" start={inView} testid="about-stat-partners" />
    </div>
  );
}
